import { Checkbox, Input } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import {
  ColumnsPicker,
  EnumSelect,
  ListHeaderFields,
  MultiSelect,
  Repeater,
  SelectionFields,
} from '../BlockFields.js';
import { useBlockOptions } from '../useBlockOptions.js';
import type { BlockFormProps } from './types.js';

/**
 * Do'kon bloklari — 5 ta.
 *
 * DIQQAT: «narx ko'rsatilsinmi» sozlamasi YO'Q (ADR-004). Narx
 * `price === null` bo'lsa saytda avtomatik «Narxni so'rang» ga aylanadi;
 * uni blok darajasida yashirish narx siyosatini ikki joyda boshqarishga
 * olib kelardi.
 */

// ─── 21. Mahsulotlar to'plami ────────────────────────────────────────────────

export function ProductsGridForm({ data, onChange }: BlockFormProps<'products-grid'>) {
  const categories = useBlockOptions('categories', data.selection.mode === 'category');
  const brands = useBlockOptions('brands', data.selection.mode === 'brand');
  const products = useBlockOptions('products', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        modes={['auto', 'featured', 'category', 'brand', 'manual']}
        targets={data.selection.mode === 'brand' ? brands.options : categories.options}
        targetLabel={data.selection.mode === 'brand' ? 'Brend' : 'Kategoriya'}
        items={products.options}
        itemsLabel="Mahsulotlar"
      />
      {data.selection.mode !== 'manual' && (
        <EnumSelect
          label="Saralash"
          value={data.sort}
          options={[
            { value: 'popular', label: 'Ommabop' },
            { value: 'newest', label: 'Yangi qo‘shilganlar' },
            { value: 'price-asc', label: 'Narx: arzondan qimmatga' },
            { value: 'price-desc', label: 'Narx: qimmatdan arzonga' },
          ]}
          onChange={(sort) => onChange({ ...data, sort })}
        />
      )}
      <ColumnsPicker
        value={data.columns}
        options={[2, 3, 4]}
        onChange={(columns) => onChange({ ...data, columns: columns as 2 | 3 | 4 })}
      />
    </>
  );
}

// ─── 22. Mahsulotlar karuseli ────────────────────────────────────────────────

export function ProductSliderForm({ data, onChange }: BlockFormProps<'product-slider'>) {
  const categories = useBlockOptions('categories', data.selection.mode === 'category');
  const brands = useBlockOptions('brands', data.selection.mode === 'brand');
  const products = useBlockOptions('products', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        modes={['auto', 'featured', 'category', 'brand', 'manual']}
        targets={data.selection.mode === 'brand' ? brands.options : categories.options}
        targetLabel={data.selection.mode === 'brand' ? 'Brend' : 'Kategoriya'}
        items={products.options}
        itemsLabel="Mahsulotlar"
      />
      <Checkbox
        label="Avtomatik aylanish"
        checked={data.autoplay}
        onChange={(event) => onChange({ ...data, autoplay: event.target.checked })}
      />
    </>
  );
}

// ─── 23. Kategoriyalar ───────────────────────────────────────────────────────

export function CategoriesGridForm({ data, onChange }: BlockFormProps<'categories-grid'>) {
  const categories = useBlockOptions('categories', true);

  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <MultiSelect
        label="Kategoriyalar"
        max={24}
        selected={data.categoryIds}
        options={categories.options}
        onChange={(categoryIds) => onChange({ ...data, categoryIds })}
        hint="Bo‘sh qoldirilsa barcha yuqori darajali kategoriyalar chiqadi"
      />
      <EnumSelect
        label="Ko‘rinishi"
        value={data.layout}
        options={[
          { value: 'grid', label: 'Kartochkalar' },
          { value: 'tiles', label: 'Kafel (rasm ustida nom)' },
        ]}
        onChange={(layout) => onChange({ ...data, layout })}
      />
      <ColumnsPicker
        value={data.columns}
        options={[2, 3, 4, 6]}
        onChange={(columns) => onChange({ ...data, columns: columns as 2 | 3 | 4 | 6 })}
      />
      <Checkbox
        label="Mahsulotlar soni ko‘rsatilsin"
        checked={data.showProductCount}
        onChange={(event) => onChange({ ...data, showProductCount: event.target.checked })}
      />
    </>
  );
}

// ─── 24. Brendlar ────────────────────────────────────────────────────────────

export function BrandsStripForm({ data, onChange }: BlockFormProps<'brands-strip'>) {
  const brands = useBlockOptions('brands', true);

  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <MultiSelect
        label="Brendlar"
        max={24}
        selected={data.brandIds}
        options={brands.options}
        onChange={(brandIds) => onChange({ ...data, brandIds })}
        hint="Bo‘sh qoldirilsa barcha brendlar chiqadi"
      />
      <Checkbox
        label="Aylanuvchi qator (karusel)"
        checked={data.isCarousel}
        onChange={(event) => onChange({ ...data, isCarousel: event.target.checked })}
      />
      <Checkbox
        label="Kulrang qilib ko‘rsatilsin"
        checked={data.grayscale}
        onChange={(event) => onChange({ ...data, grayscale: event.target.checked })}
      />
    </>
  );
}

// ─── 25. Taqqoslash ──────────────────────────────────────────────────────────

export function ProductCompareForm({ data, onChange }: BlockFormProps<'product-compare'>) {
  const products = useBlockOptions('products', true);

  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <MultiSelect
        label="Mahsulotlar"
        max={4}
        selected={data.productIds}
        options={products.options}
        onChange={(productIds) => onChange({ ...data, productIds })}
        hint="2 tadan 4 tagacha. 2 tadan kam bo‘lsa blok saytda ko‘rinmaydi"
      />
      <Repeater
        label="Taqqoslanadigan xarakteristikalar"
        max={30}
        addLabel="Qator qo‘shish"
        items={data.specKeys}
        getId={(_, index) => `spec-${index}`}
        summary={(key, index) => key || `${index + 1}-qator`}
        create={() => ''}
        onChange={(specKeys) => onChange({ ...data, specKeys })}
        emptyHint="Bo‘sh qoldirilsa mahsulotlarning barcha umumiy xarakteristikalari chiqadi"
        renderItem={(key, update) => (
          <Input
            label="Xarakteristika kaliti"
            value={key}
            placeholder="flight-time"
            onChange={(event) => update(event.target.value)}
            hint="Mahsulot kartochkasidagi spec konstruktoridagi kalit bilan bir xil bo‘lishi kerak"
          />
        )}
      />
    </>
  );
}
